import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ShoppingCart, Calendar, X, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/nextlevelgames-api';
import StoreBadge from '../components/StoreBadge';
import { classifyEvent } from '../utils/eventGames';

const TABS = [
  { key: 'muncie', label: 'Muncie Events', locationName: 'Next Level Games Muncie' },
  { key: 'carmel', label: 'Carmel Events', locationName: 'Next Level Games Carmel' },
];

const formatEventDate = (isoString, timezone) => {
  if (!isoString) return null;
  try {
    return new Date(isoString).toLocaleString('en-US', {
      timeZone: timezone || 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

const formatTimeRange = (startIso, endIso, timezone) => {
  if (!startIso) return null;
  const tz = timezone || 'America/New_York';
  try {
    const start = new Date(startIso).toLocaleString('en-US', {
      timeZone: tz, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
    const startTime = new Date(startIso).toLocaleString('en-US', {
      timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
    const endTime = endIso
      ? new Date(endIso).toLocaleString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
      : null;
    return { date: start, time: endTime ? `${startTime} – ${endTime}` : startTime };
  } catch {
    return null;
  }
};

const Events = () => {
  const { addToCart } = useCart();

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const loc = searchParams.get('location');
    return loc === 'carmel' ? 'carmel' : 'muncie';
  });
  const storeName = activeTab === 'carmel' ? 'Carmel' : 'Muncie';
  const [events, setEvents] = useState([]);
  const [gameFilter, setGameFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [addedEventId, setAddedEventId] = useState(null);
  const toastTimer = useRef(null);

  // Game each event is for, and the filter options that actually occur in this store's events
  const taggedEvents = useMemo(
    () => events.map((event) => ({ event, game: classifyEvent(event) })),
    [events]
  );
  const gameOptions = useMemo(() => {
    const counts = new Map();
    taggedEvents.forEach(({ game }) => {
      counts.set(game.key, { game, count: (counts.get(game.key)?.count || 0) + 1 });
    });
    // Most events first; "Other" always last
    return [...counts.values()].sort((a, b) => {
      if (a.game.key === 'other') return 1;
      if (b.game.key === 'other') return -1;
      return b.count - a.count || a.game.label.localeCompare(b.game.label);
    });
  }, [taggedEvents]);
  const visibleEvents = useMemo(
    () => taggedEvents.filter(({ game }) => gameFilter === 'all' || game.key === gameFilter),
    [taggedEvents, gameFilter]
  );

  // Switching store can leave a filter that has no events there; fall back to All
  useEffect(() => {
    if (gameFilter !== 'all' && !gameOptions.some(({ game }) => game.key === gameFilter)) {
      setGameFilter('all');
    }
  }, [gameOptions, gameFilter]);

  useEffect(() => {
    apiService.getLocations().then(setLocations).catch(() => {});
  }, []);

  useEffect(() => {
    const loc = searchParams.get('location');
    setActiveTab(loc === 'carmel' ? 'carmel' : 'muncie');
  }, [searchParams]);

  const fetchEvents = useCallback(async (locationName) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getEvents([locationName]);
      setEvents(data.items || []);
    } catch {
      setError('Failed to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const tab = TABS.find(t => t.key === activeTab);
    fetchEvents(tab.locationName);
  }, [activeTab, fetchEvents]);

  // Close modal on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedEvent(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const resolveLocationId = (eventLocationName) => {
    // Fallback only — see handleReserve. Needs `locations`, which arrives from
    // a separate fetch, so it returns null until that lands.
    if (!eventLocationName || !locations.length) return null;
    const nameLower = eventLocationName.toLowerCase();
    const match = locations.find(loc => {
      const locLower = (loc.name || '').toLowerCase();
      if (nameLower.includes('carmel')) return locLower.includes('carmel');
      return !locLower.includes('carmel');
    });
    return match?.id || null;
  };

  const handleReserve = (event, e) => {
    e?.stopPropagation();
    // The API resolves the event's location and returns it in the same
    // response that rendered this card. Deriving it here instead meant
    // depending on the separate /locations/list fetch: click Reserve before
    // that landed and the cart line got no location, which failed at checkout
    // with "No location ID available for order" and stayed broken because the
    // cart persists in localStorage.
    const locationId = event.location_id
      || event.present_at_location_ids?.[0]
      || resolveLocationId(event.event_location_name);
    addToCart({
      id: event.variation_id,
      name: event.name,
      price: event.price,
      image: event.imageUrl,
      variation_id: event.variation_id,
      location_id: locationId,
      present_at_location_ids: event.present_at_location_ids?.length
        ? event.present_at_location_ids
        : (locationId ? [locationId] : []),
      quantities_by_location: {},
      is_event: true,
    });
    setAddedEventId(event.id);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setAddedEventId(null), 3000);
  };

  // Seats left, or null when the event doesn't track stock (Square shows just "Available").
  const seatsLeft = (event) => {
    const byLocation = event.quantities_by_location || {};
    const locId = event.location_id || event.present_at_location_ids?.[0];
    const qty = locId in byLocation ? byLocation[locId] : null;
    return qty > 0 ? qty : null;
  };

  // Same green pill as the in-stock badges: seats left when stock is tracked,
  // otherwise just "Available" (nothing when sold out).
  const SeatsLeft = ({ event }) => {
    if (isSoldOut(event)) return null;
    const seats = seatsLeft(event);
    return <StoreBadge name={seats === null ? 'Available' : `${seats} ${seats === 1 ? 'seat' : 'seats'} left`} />;
  };

  const isSoldOut = (event) => {
    const soldOut = event.sold_out_location_ids || [];
    const present = event.present_at_location_ids || [];
    if (soldOut.length === 0) return false;
    // If present_at_all_locations or no specific locations listed, check if ALL known sold-out
    // locations cover it — but we can't know "all" locations, so only mark sold out when
    // specific present locations are listed and every one of them is sold out.
    if (present.length === 0) return false;
    return present.every(id => soldOut.includes(id));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      {/* Toast */}
      <AnimatePresence>
        {addedEventId && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg text-sm font-semibold"
          >
            <CheckCircle className="w-4 h-4" />
            Added to cart!
          </motion.div>
        )}
      </AnimatePresence>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (() => {
          const event = selectedEvent;
          const soldOut = isSoldOut(event);
          const timeRange = formatTimeRange(event.event_start_at, event.event_end_at, event.event_timezone);
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50"
              onClick={() => setSelectedEvent(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                {event.imageUrl && (
                  <div className="relative h-56 bg-gray-200 overflow-hidden rounded-t-2xl">
                    <img src={event.imageUrl} alt={event.name} className="w-full h-full object-cover" />
                    {soldOut && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">Sold Out</div>
                    )}
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-xl font-bold leading-tight pr-4">{event.name}</h2>
                    <button onClick={() => setSelectedEvent(null)} className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 transition-colors">
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  <p className="text-2xl font-bold text-primary-600 mb-5">
                    {event.price > 0 ? `$${event.price.toFixed(2)}` : 'Free'}
                  </p>
                  {timeRange && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">Date & Time</h3>
                      <p className="text-sm text-gray-600">{timeRange.date}</p>
                      <p className="text-sm text-gray-600">{timeRange.time}</p>
                    </div>
                  )}
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Location</h3>
                    <div className="flex flex-wrap gap-2">
                      <StoreBadge name={storeName} />
                      <SeatsLeft event={event} />
                    </div>
                  </div>
                  {event.description && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Details</h3>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{event.description}</p>
                    </div>
                  )}
                  <button
                    onClick={(e) => { handleReserve(event, e); setSelectedEvent(null); }}
                    disabled={soldOut}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${
                      soldOut ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    {soldOut ? 'Sold Out' : 'Reserve Your Seat'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      <div className="container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="heading-block text-4xl mb-4">
            Upcoming Events - {storeName}
          </h1>
        </motion.div>

        {/* Game filter */}
        {!loading && !error && gameOptions.length > 1 && (
          <div className="flex flex-nowrap overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0 sm:pb-0 sm:flex-wrap sm:justify-center gap-2 mb-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="group" aria-label="Filter events by game">
            {[{ key: 'all', label: 'All', count: events.length }, ...gameOptions.map(({ game, count }) => ({ key: game.key, label: game.label, count }))].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setGameFilter(opt.key)}
                aria-pressed={gameFilter === opt.key}
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                  gameFilter === opt.key
                    ? 'bg-primary-600 border-primary-600 text-white shadow'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {opt.label}
                <span className={`ml-1.5 text-xs font-normal ${gameFilter === opt.key ? 'text-white/80' : 'text-gray-400'}`}>{opt.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Events Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 text-lg">{error}</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No upcoming events at this location.</p>
          </div>
        ) : (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {visibleEvents.map(({ event, game }, index) => {
              const soldOut = isSoldOut(event);
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="relative h-48 bg-gray-200 overflow-hidden">
                    {event.imageUrl ? (
                      <img src={event.imageUrl} alt={event.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-5xl">🎮</div>
                    )}
                    {soldOut && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">Sold Out</div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="inline-block w-fit text-xs font-semibold text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full mb-2">{game.label}</span>
                    <h3 className="text-lg font-bold mb-2 leading-tight">{event.name}</h3>
                    {event.event_start_at && (
                      <div className="flex items-center text-gray-500 text-xs mb-1">
                        <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span>{formatEventDate(event.event_start_at, event.event_timezone)}</span>
                      </div>
                    )}
                    {event.description && (
                      <p className="text-sm text-gray-600 mt-2 mb-4 line-clamp-3 flex-1">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <StoreBadge name={storeName} />
                      <SeatsLeft event={event} />
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                      <span className="text-lg font-bold text-primary-600">
                        {event.price > 0 ? `$${event.price.toFixed(2)}` : 'Free'}
                      </span>
                      <button
                        onClick={(e) => handleReserve(event, e)}
                        disabled={soldOut}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                          soldOut ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {soldOut ? 'Sold Out' : 'Reserve Your Seat'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Events;
